import {observer} from 'mobx-react-lite'
import React, {memo, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Hub} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Icon} from '@components/common/base/icon'
import {
  useTimelineConfig,
  useTimelineNewStore
} from '@components/timeline-new/timeline-new-store'
import {ColumnId} from '@components/timeline-new/types'
import {useSyncEventUpdateEffect} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Sticker} from '@extensions/sticker/sticker'
import {UserIcon} from '@extensions/sticker/views-web/user/user-sticker-state'
import {AssignedPopup} from './assinged-popup'
import {ColumnProps} from './types'

export const Assigned = memo(({item}: ColumnProps) => {
  const {hubId, type, path} = item
  useSyncEventUpdateEffect('current', {filter: hubId, deps: [hubId]})

  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(hubId)

  if (!hub) {
    return null
  }

  if (type === 'column') {
    return null // case for aggregated avatars
  } else if (type === 'task') {
    return <AssignedTaskComponent hub={hub} path={path} />
  }

  return null
})

const AssignedTaskComponent = observer((props: {hub: Hub; path: string}) => {
  const [state, store] = useTimelineNewStore()
  const config = useTimelineConfig()
  const [reference, setReference] = useState<HTMLDivElement | null>(null)
  const {hub, path} = props

  useSyncEventUpdateEffect('current', {filter: hub.id})

  const users = Sticker.getUsers(hub)
  const shift = 12

  const editable =
    !config.static && PermissionsChecker.checkEditTaskStickers(hub, 'not-user')

  const editing =
    state.editingPath === path && state.editingColumn === ColumnId.assigned

  const hasUsers = users.length > 0
  const avatarGroupWidth = getAvatarGroupWidth(users.length)

  return (
    <div
      className="flex relative"
      onClick={e => {
        e.stopPropagation()
        store.startEditing(hub.id, path, ColumnId.assigned)
      }}
    >
      <div
        ref={setReference}
        className="w-24 h-full absolute right-0 top-0 z-[-1]"
      />
      {hasUsers && (
        <div
          className={`flex flex-row-reverse z-0`}
          style={{width: `${avatarGroupWidth}px`}}
        >
          {users.slice(0, 3).map((userId, ix) => (
            <div
              key={userId}
              className={cn(
                'relative flex rounded-full p-2 bg-background-transparent items-center',
                !config.static && 'cursor-pointer'
              )}
              style={{
                left: shift * ix,
                zIndex: 100 + ix
              }}
            >
              <UserIcon
                userId={userId}
                size={20}
                hideTitle
                cursor={!config.static ? 'pointer' : 'default'}
              />
            </div>
          ))}
        </div>
      )}

      {!hasUsers && !config.static && (
        <div
          className={cn(
            editing ? 'flex' : 'hidden',
            'items-center justify-center group-hover/row:flex w-24'
          )}
        >
          <TwUserIcon
            size={20}
            icon="IconCustomUserCircleDashed20"
            $editing={editing}
          />
        </div>
      )}

      {editable && reference && hub && editing && (
        <AssignedPopup hub={hub} reference={reference} />
      )}
    </div>
  )
})

export const TwUserIcon = tw(Icon)<{$editing: boolean}>`
  hover:!text-action-hover
  ${p => (p.$editing ? '!text-action-hover' : '!text-secondary')}
`
function getAvatarGroupWidth(length: number): number {
  if (length === 0) {
    return 0
  }

  if (length === 1) {
    return 24
  }

  const totalLen = Math.min(3, length)

  return (totalLen - 1) * 12 + 24
}
